import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaExclamationTriangle } from 'react-icons/fa';

import Modal from '../../../components/Modal/Modal';
import { GhostButton } from '../../../styles/ui/Button';
import { permissionDisplayName } from '../permissionModel';
import { DiffCountPill, GREEN, RED, SensitivePill } from '../styles';

// ---------------------------------------------------------------------------
// Modal de alterações não salvas (v2 do handoff, 2026-09-15) — a lista que
// antes morava inline na coluna direita. Usa o Modal compartilhado (H.2:
// nunca forjar modal/backdrop/foco à mão); o pill "+X −Y" vai no `subtitle`
// porque o `title` do primitivo é string (vira aria-label). O corpo rola num
// container próprio — a lista escala para qualquer volume de alterações.
// ---------------------------------------------------------------------------

const ScrollArea = styled.div`
   max-height: calc(100vh - 280px);
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 14px;
`;

const GroupHead = styled.div`
   display: flex;
   align-items: baseline;
   gap: 8px;

   strong {
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #8a8a8a;
   }
   span {
      font-size: 11px;
      color: #9a9a9a;
   }
`;

const ItemList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-top: 6px;
`;

const ItemCard = styled.div`
   display: flex;
   align-items: center;
   gap: 9px;
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 10px;
   padding: 9px 11px;
`;

const Tag = styled.span`
   flex: 0 0 auto;
   width: 18px;
   height: 18px;
   border-radius: 5px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 13px;
   font-weight: 800;
   background: ${({ $type }) => ($type === 'added' ? GREEN.bgTag : RED.bgTag)};
   color: ${({ $type }) => ($type === 'added' ? GREEN.text : RED.text)};
`;

const ItemText = styled.div`
   flex: 1 1 auto;
   min-width: 0;

   strong {
      display: block;
      font-size: 12.5px;
      font-weight: 700;
      color: #222;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }
   small {
      display: block;
      font-size: 11px;
      color: #8a8a8a;
      font-variant-numeric: tabular-nums;
   }
`;

const EmptyBox = styled.div`
   border: 1px dashed rgba(0, 0, 0, 0.14);
   border-radius: 10px;
   padding: 12px;
   font-size: 12px;
   color: #777;
   text-align: center;
`;

function pluralPerms(count) {
   return `${count} ${count === 1 ? 'permissão' : 'permissões'}`;
}

function Group({ label, type, perms }) {
   if (!perms.length) return null;
   return (
      <div>
         <GroupHead>
            <strong>{label}</strong>
            <span>{pluralPerms(perms.length)}</span>
         </GroupHead>
         <ItemList>
            {perms.map((perm) => (
               <ItemCard key={perm.id}>
                  <Tag $type={type}>{type === 'added' ? '+' : '−'}</Tag>
                  <ItemText>
                     <strong>{permissionDisplayName(perm)}</strong>
                     <small>{perm.key}</small>
                  </ItemText>
                  {perm.isSensitive && (
                     <SensitivePill>
                        <FaExclamationTriangle />
                        Sensível
                     </SensitivePill>
                  )}
               </ItemCard>
            ))}
         </ItemList>
      </div>
   );
}

Group.propTypes = {
   label: PropTypes.string.isRequired,
   type: PropTypes.oneOf(['added', 'removed']).isRequired,
   perms: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default function ChangesModal({ isOpen, onClose, added, removed }) {
   const dirty = added.length + removed.length > 0;

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Alterações não salvas"
         subtitle={
            <DiffCountPill $active={dirty}>
               +{added.length} −{removed.length}
            </DiffCountPill>
         }
         maxWidth="560px"
         footer={
            <GhostButton type="button" onClick={onClose}>
               Fechar
            </GhostButton>
         }
      >
         <ScrollArea>
            {!dirty && (
               <EmptyBox>Nada alterado desde o último salvamento.</EmptyBox>
            )}
            <Group label="Concedidas" type="added" perms={added} />
            <Group label="Removidas" type="removed" perms={removed} />
         </ScrollArea>
      </Modal>
   );
}

ChangesModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   added: PropTypes.arrayOf(PropTypes.object).isRequired,
   removed: PropTypes.arrayOf(PropTypes.object).isRequired,
};
